/**
 * Un hallazgo es UN dato encontrado por UNA fuente. Nada más.
 *
 * Es la pieza que faltaba. Hasta ahora cada camino de lectura devolvía un objeto
 * de formulario ya armado, y juntarlos era pisarlos: cargar el dorso borraba lo
 * que había dado el frente. Con hallazgos eso no se puede: cada fuente agrega los
 * suyos a una lista y nadie borra a nadie. La decisión de qué vale más se toma
 * UNA vez, al consolidar, y con una regla escrita.
 */
import { CampoLeido, LecturaDeFuente, OrigenDeLectura, Rectangulo, Verificacion } from "./types";
import { CONFIANZA } from "./tuning";
import { esLaMismaLectura } from "./texto";

export interface Hallazgo {
  /** Nombre del campo. Es la clave con la que sale en el JSON. */
  campo: string;
  /** Valor ya normalizado, listo para comparar y para volcar. */
  valor: string;
  /** Tal como se leyó, sin tocar. Sirve para mostrar un desacuerdo. */
  crudo: string;
  origen: OrigenDeLectura;
  /** Dónde está en la imagen, para poder señalarlo. */
  caja?: Rectangulo;
  /**
   * Un dígito de control probó esta lectura. No es "mucha confianza": es que la
   * aritmética cierra. Lo declara quien conoce el formato, porque no se puede
   * inferir desde acá: el verificador compuesto del TD1 cubre el número y las
   * fechas, y NO cubre el sexo ni la nacionalidad.
   */
  probado?: boolean;
  confianzaOcr?: number;
  /**
   * La fuente sabe que su valor puede venir CORTADO O ABREVIADO. Es el caso de
   * la línea 3 del MRZ, que tiene 30 caracteres y trunca —"MARIANA FLORENCIA"
   * sale "MARIANA<FL"—, y el de la nacionalidad, que el MRZ entrega como código
   * de tres letras y la tarjeta imprime entera: "ARG" y "ARGENTINA".
   *
   * Un valor así no debe pisar al completo aunque venga de una fuente probada,
   * y tampoco debe contar como un desacuerdo con él.
   *
   * Lo declara la fuente. Que una lectura sea prefijo de otra NO alcanza: el
   * ruido del OCR también produce prefijos, y por ese camino un "BERTOLDIK" le
   * ganaba a un "BERTOLDI" probado por el código de barras.
   */
  puedeEstarTruncado?: boolean;
}

const soloLetrasYNumeros = (v: string) => v.toUpperCase().replace(/[^A-Z0-9]/g, "");

/** ¿`corto` es el principio de `largo`? Es como se reconoce un valor truncado. */
export function esPrefijoDe(corto: string, largo: string): boolean {
  const a = soloLetrasYNumeros(corto);
  const b = soloLetrasYNumeros(largo);
  return a.length > 0 && b.length > a.length && b.startsWith(a);
}

/**
 * Junta los hallazgos de un campo en un valor, con sus fuentes y su verificación.
 *
 * Las reglas, en orden, y todas por un motivo medido:
 *
 *  1. Un valor TRUNCADO no le gana a uno completo del que es prefijo, ni
 *     siquiera viniendo del MRZ. El MRZ prueba lo que alcanzó a escribir, y la
 *     línea 3 corta a los 30 caracteres: "MARIANA<FL" es correcto y es menos
 *     información que el "MARIANA FLORENCIA" impreso al lado.
 *  2. Un dígito de control gana a todo lo demás, incluido a una lectura sin
 *     prueba que diga otra cosa. Un verificador cierra o no cierra.
 *  3. Dos fuentes independientes que coinciden valen más que una sola segura.
 *     Se cuentan orígenes, no hallazgos.
 *  4. Si se contradicen dos fuentes del mismo peso, no se promedia: se declara
 *     el conflicto y se muestran las dos lecturas.
 */
export function consolidarCampo(hallazgos: Hallazgo[]): CampoLeido {
  const utiles = hallazgos.filter((h) => h.valor.trim() !== "");
  if (utiles.length === 0) {
    return { valor: null, fuentes: [], verificacion: "lectura_unica", confianza: 0 };
  }

  const fuentes: LecturaDeFuente[] = utiles.map((h) => ({
    origen: h.origen,
    valorLeido: h.crudo,
    confianzaOcr: h.confianzaOcr ?? null,
  }));

  // Regla 1: los truncados no compiten; acompañan al valor completo.
  //
  // Truncado lo DECLARA la fuente, no lo adivina el consolidador. Ser prefijo de
  // otra lectura no alcanza: medido, el código de barras entregó "BERTOLDI" con
  // su corrección de errores y el MRZ "BERTOLDIK" —un '<' leído como K—, y sin
  // esta condición el valor probado quedaba afuera por ser prefijo de la basura
  // y se mostraba "BERTOLDIK" con cara de verificado.
  const completos = utiles.filter(
    (h) =>
      !(h.puedeEstarTruncado && utiles.some((otro) => otro !== h && esPrefijoDe(h.valor, otro.valor)))
  );
  const enJuego = completos.length > 0 ? completos : utiles;

  // Regla 2: si hay un dígito de control, decide él. Una lectura SIN prueba que
  // diga otra cosa no lo baja a "en conflicto": el verificador cierra o no
  // cierra, y si cierra el dato es ese. Esto importa desde que buscamos los
  // valores probados adentro del impreso: el OCR de una foto blanda devuelve
  // "19 MAR 5010" donde dice 2010, y eso no puede tirar abajo una fecha que su
  // propio dígito probó. El desacuerdo no se esconde: queda en `fuentes`.
  const probados = enJuego.filter((h) => h.probado);
  const enDisputa = probados.length > 0 ? probados : enJuego;

  // Un CONFLICTO es que dos fuentes digan cosas DISTINTAS, no que una tenga una
  // letra mal. Medido sobre el frente de 2010: el MRZ dio "RODOLFO MANUEL" y
  // el impreso "RODOLFO MANUEI", y declarar eso conflicto borraba de la
  // pantalla un nombre que estaba entero. Con ruido, el dato es el mismo y lo
  // que no hay es una coincidencia que valga como segunda fuente.
  const valores = enDisputa.map((h) => soloLetrasYNumeros(h.valor));
  const distintos = new Set(valores);
  const soloRuido =
    distintos.size > 1 && valores.every((v) => esLaMismaLectura(v, valores[0]));
  if (distintos.size > 1 && !soloRuido) {
    return { valor: null, fuentes, verificacion: "en_conflicto", confianza: CONFIANZA.enConflicto };
  }

  // El valor que se muestra es el más largo: entre "MARIANA FLORENCIA" y su
  // prefijo probado, la información completa es la útil.
  //
  // Cuando la diferencia es ruido decide el ORIGEN, no el largo: el MRZ y los
  // códigos 2D están hechos para leerse a máquina y el impreso no.
  const porOrigen = (h: Hallazgo) => (h.origen === "impreso" ? 1 : 0);
  const elegido = soloRuido
    ? enDisputa.reduce((mejor, h) => (porOrigen(h) < porOrigen(mejor) ? h : mejor))
    : enDisputa.reduce((mejor, h) => (h.valor.length > mejor.valor.length ? h : mejor));

  // Regla 3: dos fuentes que COINCIDEN. Se cuentan ORÍGENES distintos, no
  // hallazgos: el impreso puede aportar el mismo campo dos veces —está en la
  // tarjeta más de una vez— y eso no es una segunda fuente, es la misma.
  const probado = utiles.some((h) => h.probado);
  const origenes = new Set(
    utiles
      .filter((h) => soloLetrasYNumeros(h.valor) === soloLetrasYNumeros(elegido.valor) ||
        esPrefijoDe(h.valor, elegido.valor))
      .map((h) => h.origen)
  );
  const coinciden = !soloRuido && origenes.size > 1;

  const verificacion: Verificacion = probado
    ? "digito_de_control"
    : coinciden
      ? "coincidencia_multiple"
      : "lectura_unica";

  const confianza = probado
    ? CONFIANZA.digitoDeControl
    : coinciden
      ? CONFIANZA.coincidenciaMultiple
      : Math.min(CONFIANZA.topeLecturaUnica, elegido.confianzaOcr ?? CONFIANZA.topeLecturaUnica);

  return { valor: elegido.valor, fuentes, verificacion, confianza };
}

/** Agrupa por campo y consolida cada uno. */
export function consolidar(hallazgos: Hallazgo[]): Record<string, CampoLeido> {
  const porCampo = new Map<string, Hallazgo[]>();
  for (const h of hallazgos) {
    const lista = porCampo.get(h.campo) ?? [];
    lista.push(h);
    porCampo.set(h.campo, lista);
  }
  const salida: Record<string, CampoLeido> = {};
  for (const [campo, lista] of porCampo) salida[campo] = consolidarCampo(lista);
  return salida;
}

/** La caja de un campo, para señalarlo: la del hallazgo que tenga una. */
export function cajaDe(hallazgos: Hallazgo[], campo: string): Rectangulo | null {
  return hallazgos.find((h) => h.campo === campo && h.caja)?.caja ?? null;
}
